import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest, buildApiUrl } from '../../api/client';
import TechnicianBuildingDevicesBlock from '../../components/technician/TechnicianBuildingDevicesBlock';
import ChecklistDevicePicker, { type ChecklistPickerDeviceRow } from '../../components/inspection/ChecklistDevicePicker';
import { formatDeviceTypeLabel } from '../admin/devices/deviceTypes';
import '../admin/quotes/quote-generator.css';

type DevicePopulated = {
    _id?: string;
    type?: string;
    manufacturer?: string;
    modelName?: string;
    modelNumber?: string;
    serialNumber?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    lastInspectedAt?: string | null;
    lastInspectionResult?: string;
    photos?: string[];
    notes?: string;
};

type InspectionItem = {
    itemId: string;
    label: string;
    category: string;
    testType?: string;
    required?: boolean;
    result: 'pass' | 'fail' | 'deficient' | 'n/a' | null;
    note?: string;
    photoUrl?: string;
    deviceId?: string | DevicePopulated | null;
};

type Inspection = {
    _id: string;
    siteAddress?: string;
    status: 'in_progress' | 'completed';
    items: InspectionItem[];
    clientId?: string | { _id?: string; firstName?: string; lastName?: string; buildingName?: string } | null;
};

function extractClientObjectId(inv: Inspection): string | null {
    const c = inv.clientId;
    if (!c) return null;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && c !== null && '_id' in c && (c as { _id?: unknown })._id != null) {
        return String((c as { _id: string })._id);
    }
    return null;
}

type ItemUpdates = Record<string, { result?: 'pass' | 'fail' | 'deficient' | 'n/a'; note?: string; photoUrl?: string }>;

export default function InspectionChecklistLegacy(): React.JSX.Element {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    /** Checklist is technician-only; admins use /admin/inspections/:id/field + summary. */
    const basePath = '/technician/inspections';

    const [inspection, setInspection] = useState<Inspection | null>(null);
    const [pending, setPending] = useState<ItemUpdates>({});
    const [completing, setCompleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [deficiencyByItemId, setDeficiencyByItemId] = useState<Record<string, { _id: string }>>({});
    const [deficiencyDraft, setDeficiencyDraft] = useState<
        Record<string, { description: string; severity: 'critical' | 'major' | 'minor'; saving?: boolean }>
    >({});
    const [devicePanelDevice, setDevicePanelDevice] = useState<DevicePopulated | null>(null);
    const [workflowTab, setWorkflowTab] = useState<'checklist' | 'devices'>('checklist');
    const [devicesCatalog, setDevicesCatalog] = useState<ChecklistPickerDeviceRow[]>([]);
    const [deviceLinkSavingItemId, setDeviceLinkSavingItemId] = useState<string | null>(null);

    function resolvedDeviceBrief(item: InspectionItem): DevicePopulated | null {
        const raw = item.deviceId;
        if (!raw || typeof raw === 'string') return null;
        return raw as DevicePopulated;
    }

    function deviceIdForApi(item: InspectionItem): string | undefined {
        const raw = item.deviceId;
        if (!raw) return undefined;
        if (typeof raw === 'string') return raw;
        if (typeof raw === 'object' && raw !== null && '_id' in raw && (raw as { _id?: string })._id) {
            return String((raw as { _id: string })._id);
        }
        return undefined;
    }

    const loadDeficiencies = useCallback(() => {
        if (!id) return;
        apiRequest<{ success: boolean; data: Array<{ _id: string; checklistItemId: string }> }>(
            `/api/deficiencies?inspectionId=${encodeURIComponent(id)}`
        )
            .then((res) => {
                const map: Record<string, { _id: string }> = {};
                for (const d of res.data || []) {
                    if (d.checklistItemId) map[d.checklistItemId] = { _id: d._id };
                }
                setDeficiencyByItemId(map);
            })
            .catch(() => setDeficiencyByItemId({}));
    }, [id]);

    const load = useCallback(() => {
        apiRequest<{ success?: boolean; data: Inspection }>(`/api/inspections/${id}`)
            .then((res) => {
                const row = res.data;
                setInspection(row);
                if (row.status === 'completed') {
                    navigate(`${basePath}/${id}/summary`, { replace: true });
                }
            })
            .catch((err: unknown) => {
                setErrorMessage(err instanceof Error ? err.message : 'Failed to load inspection.');
            });
    }, [id, basePath, navigate]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadDeficiencies(); }, [loadDeficiencies]);

    const catalogClientKey = inspection ? extractClientObjectId(inspection) : null;
    useEffect(() => {
        if (!catalogClientKey) {
            setDevicesCatalog([]);
            return;
        }
        apiRequest<{ success: boolean; data: ChecklistPickerDeviceRow[] }>(
            `/api/devices?clientId=${encodeURIComponent(catalogClientKey)}`
        )
            .then((res) => {
                setDevicesCatalog(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => setDevicesCatalog([]));
    }, [inspection?._id, catalogClientKey, workflowTab]);

    async function setItemLinkedDevice(itemId: string, deviceId: string | null) {
        if (!id) return;
        setDeviceLinkSavingItemId(itemId);
        setErrorMessage(null);
        try {
            const res = await apiRequest<{ success: boolean; data: Inspection }>(`/api/inspections/${id}/items`, {
                method: 'PATCH',
                json: { items: [{ itemId, deviceId }] }
            });
            setInspection(res.data);
        } catch (err: unknown) {
            setErrorMessage(err instanceof Error ? err.message : 'Could not link device.');
        } finally {
            setDeviceLinkSavingItemId(null);
        }
    }

    async function saveItem(itemId: string, patch: { result?: 'pass' | 'fail' | 'deficient' | 'n/a'; note?: string; photoUrl?: string }) {
        try {
            const res = await apiRequest<{ success: boolean; data: Inspection }>(`/api/inspections/${id}/items`, {
                method: 'PATCH',
                json: { items: [{ itemId, ...patch }] }
            });
            setInspection(res.data);
        } catch (err: unknown) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to save item.');
        }
    }

    function setItemResult(itemId: string, result: 'pass' | 'fail' | 'deficient' | 'n/a') {
        const next = { ...(pending[itemId] || {}), result };
        setPending((prev) => ({ ...prev, [itemId]: next }));
        saveItem(itemId, next).catch(() => undefined);
    }

    function setItemNote(itemId: string, note: string) {
        const next = { ...(pending[itemId] || {}), note };
        setPending((prev) => ({ ...prev, [itemId]: next }));
        saveItem(itemId, next).catch(() => undefined);
    }

    function setItemPhoto(itemId: string, photoUrl: string) {
        const next = { ...(pending[itemId] || {}), photoUrl };
        setPending((prev) => ({ ...prev, [itemId]: next }));
        saveItem(itemId, next).catch(() => undefined);
    }

    async function uploadDeficiencyPhoto(file: File): Promise<string | null> {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(buildApiUrl('/api/upload'), { method: 'POST', body: formData });
        const result = await response.json();
        if (result.success && result.data?.url) return result.data.url as string;
        return null;
    }

    async function submitDeficiency(itemId: string) {
        if (!id || !inspection) return;
        const draft = deficiencyDraft[itemId];
        if (!draft?.description?.trim()) {
            setErrorMessage('Enter a deficiency description before saving.');
            return;
        }
        const line = inspection.items.find((i) => i.itemId === itemId);
        const r = line ? getItemResult(line) : null;
        if (r !== 'fail' && r !== 'deficient') {
            setErrorMessage('Mark the item as Fail or Deficient before flagging.');
            return;
        }
        setDeficiencyDraft((prev) => ({
            ...prev,
            [itemId]: { ...prev[itemId], description: draft.description, severity: draft.severity, saving: true }
        }));
        setErrorMessage(null);
        try {
            const photos: string[] = [];
            const fileInput = document.getElementById(`def-file-${itemId}`) as HTMLInputElement | null;
            const file = fileInput?.files?.[0];
            if (file) {
                const url = await uploadDeficiencyPhoto(file);
                if (url) photos.push(url);
            }
            const checklistLine = inspection?.items?.find((i) => i.itemId === itemId);
            const deviceIdPayload = checklistLine ? deviceIdForApi(checklistLine) : undefined;
            await apiRequest(`/api/deficiencies`, {
                method: 'POST',
                json: {
                    inspectionId: id,
                    checklistItemId: itemId,
                    description: draft.description.trim(),
                    severity: draft.severity,
                    photos,
                    ...(deviceIdPayload ? { deviceId: deviceIdPayload } : {})
                }
            });
            await loadDeficiencies();
            setDeficiencyDraft((prev) => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            if (fileInput) fileInput.value = '';
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not save deficiency');
        } finally {
            setDeficiencyDraft((prev) => ({
                ...prev,
                [itemId]: {
                    description: draft.description,
                    severity: draft.severity,
                    saving: false
                }
            }));
        }
    }

    function getItemResult(item: InspectionItem): 'pass' | 'fail' | 'deficient' | 'n/a' | null {
        return pending[item.itemId]?.result ?? item.result ?? null;
    }

    function getItemNote(item: InspectionItem): string {
        return pending[item.itemId]?.note ?? item.note ?? '';
    }

    async function completeInspection() {
        setCompleting(true);
        setErrorMessage(null);
        try {
            await apiRequest(`/api/inspections/${id}/complete`, { method: 'PATCH' });
            navigate(`${basePath}/${id}/summary`);
        } catch (err: unknown) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to complete inspection.');
            setCompleting(false);
        }
    }

    if (!inspection) {
        return (
            <AppShell>
                <div className="quote-page">
                    <div className="quote-container">
                        {errorMessage ? (
                            <div className="banner banner-error">{errorMessage}</div>
                        ) : (
                            <p style={{ color: 'var(--dash-muted)' }}>Loading…</p>
                        )}
                    </div>
                </div>
            </AppShell>
        );
    }

    const allItems = inspection.items;
    const categories = Array.from(new Set(allItems.map((i) => i.category)));

    const mergedItems: InspectionItem[] = allItems.map((item) => ({
        ...item,
        result: getItemResult(item) as any,
        note: getItemNote(item)
    }));

    const completedItems = mergedItems.filter((i) => i.result).length;
    const requiredItems = mergedItems.filter((i) => i.required);
    const allAnswered = requiredItems.every((i) => i.result !== null && i.result !== undefined && i.result !== ('' as any));

    const clientOid = extractClientObjectId(inspection);
    const clientName = (() => {
        const c = inspection.clientId;
        if (!c || typeof c === 'string') return '';
        return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.buildingName || '';
    })();

    return (
        <AppShell>
            <div className="quote-page" style={{ position: 'relative' }}>
                <div className="quote-container" style={{ maxWidth: '860px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}

                    <header style={{ marginBottom: '28px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <h1 style={{ fontSize: '2rem', marginBottom: '6px' }}>Inspection Checklist</h1>
                                {clientName && <p style={{ color: 'var(--dash-muted)', margin: '0 0 2px' }}>Client: {clientName}</p>}
                                {inspection.siteAddress && <p style={{ color: 'var(--dash-muted)', margin: 0 }}>Site: {inspection.siteAddress}</p>}
                                <p style={{ color: 'var(--dash-muted)', margin: '6px 0 0' }}>{completedItems} of {mergedItems.length} items completed</p>
                                <p style={{ color: 'var(--dash-muted)', margin: '6px 0 0', fontSize: '0.85rem' }}>
                                    Complete each item on site. Flag deficiencies from failed items when needed.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}/>
                        </div>
                    </header>

                    <div
                        role="tablist"
                        aria-label="Inspection workflow"
                        style={{
                            display: 'flex',
                            gap: 8,
                            marginBottom: 22,
                            padding: 4,
                            background: 'rgba(0,0,0,0.04)',
                            borderRadius: 12,
                            maxWidth: 420
                        }}
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={workflowTab === 'checklist'}
                            onClick={() => setWorkflowTab('checklist')}
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: 10,
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                background: workflowTab === 'checklist' ? '#fff' : 'transparent',
                                color: workflowTab === 'checklist' ? 'var(--dash-ink, #111)' : 'var(--dash-muted)',
                                boxShadow: workflowTab === 'checklist' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                            }}
                        >
                            Checklist
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={workflowTab === 'devices'}
                            onClick={() => setWorkflowTab('devices')}
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: 10,
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                background: workflowTab === 'devices' ? '#fff' : 'transparent',
                                color: workflowTab === 'devices' ? 'var(--dash-ink, #111)' : 'var(--dash-muted)',
                                boxShadow: workflowTab === 'devices' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                            }}
                        >
                            Building devices
                        </button>
                    </div>
                    {workflowTab === 'devices' ? <TechnicianBuildingDevicesBlock clientId={clientOid} /> : null}

                    {workflowTab === 'checklist'
                        ? (
                    <>
                    {categories.map((cat) => {
                        const catItems = mergedItems.filter((i) => i.category === cat);
                        return (
                            <div key={cat} className="quote-card" style={{ marginBottom: '20px', padding: '20px 24px' }}>
                                <div className="quote-section-title" style={{ marginBottom: '16px' }}>{cat}</div>
                                {catItems.map((item) => (
                                    <div
                                        key={item.itemId}
                                        style={{
                                            paddingBottom: '16px',
                                            marginBottom: '16px',
                                            borderBottom: '1px solid rgba(0,0,0,0.07)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                                            <span style={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                {item.label}
                                                {item.required ? <span style={{ color: '#c0392b' }}> *</span> : null}
                                                {resolvedDeviceBrief(item) ? (
                                                    <button
                                                        type="button"
                                                        title="Registered device"
                                                        onClick={() => setDevicePanelDevice(resolvedDeviceBrief(item))}
                                                        className="dash-btn dash-btn-ghost"
                                                        style={{
                                                            padding: '2px 10px',
                                                            fontSize: '0.72rem',
                                                            fontWeight: 800,
                                                            borderRadius: 999,
                                                            borderColor: '#0f766e',
                                                            color: '#0f766e'
                                                        }}
                                                    >
                                                        {formatDeviceTypeLabel(resolvedDeviceBrief(item)?.type)}{' '}
                                                        {resolvedDeviceBrief(item)?.installationLocation
                                                            ? `· ${resolvedDeviceBrief(item)?.installationLocation}`
                                                            : ''}
                                                    </button>
                                                ) : null}
                                                {deficiencyByItemId[item.itemId] ? (
                                                    <span
                                                        style={{
                                                            marginLeft: '8px',
                                                            fontSize: '0.68rem',
                                                            fontWeight: 800,
                                                            padding: '2px 8px',
                                                            borderRadius: 999,
                                                            background: 'rgba(192,57,43,0.12)',
                                                            color: '#c0392b'
                                                        }}
                                                    >
                                                        Deficiency
                                                    </span>
                                                ) : null}
                                            </span>
                                            <span style={{ background: 'rgba(46,138,87,0.1)', color: '#2e8a57', borderRadius: 999, padding: '2px 8px', fontSize: '0.72rem', marginLeft: '6px' }}>{item.testType || 'Visual'}</span>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                {(['pass', 'fail', 'deficient', 'n/a'] as const).map((result) => {
                                                    const active = getItemResult(item) === result;
                                                    const colors: Record<string, string> = {
                                                        pass: '#2e8a57',
                                                        fail: '#c0392b',
                                                        deficient: '#e6a817'
                                                    };
                                                    return (
                                                        <button
                                                            key={result}
                                                            type="button"
                                                            onClick={() => setItemResult(item.itemId, result)}
                                                            style={{
                                                                padding: '5px 14px',
                                                                borderRadius: 999,
                                                                border: `2px solid ${colors[result]}`,
                                                                background: active ? colors[result] : 'transparent',
                                                                color: active ? '#fff' : colors[result],
                                                                fontWeight: 700,
                                                                fontSize: '0.8rem',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s'
                                                            }}
                                                        >
                                                            {result.charAt(0).toUpperCase() + result.slice(1)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {(getItemResult(item) === 'deficient' || getItemResult(item) === 'fail') && (
                                            <div style={{ marginTop: '10px' }}>
                                                <label className="form-label" htmlFor={`note-${item.itemId}`}>Deficiency Note</label>
                                                <textarea
                                                    id={`note-${item.itemId}`}
                                                    className="form-input"
                                                    rows={2}
                                                    placeholder="Describe the deficiency…"
                                                    value={getItemNote(item)}
                                                    onChange={(e) => setItemNote(item.itemId, e.target.value)}
                                                    style={{ resize: 'vertical', width: '100%' }}
                                                />
                                            </div>
                                        )}

                                        <ChecklistDevicePicker
                                            categoryLabel={cat}
                                            itemLabel={item.label}
                                            devices={devicesCatalog}
                                            valueId={deviceIdForApi(item)}
                                            onCommit={(nextId) => void setItemLinkedDevice(item.itemId, nextId)}
                                            saving={deviceLinkSavingItemId === item.itemId}
                                            disabledReason={
                                                !clientOid ? 'Inspection must be linked to a client before devices can attach.' : null
                                            }
                                        />

                                        <div style={{ marginTop: '8px' }}>
                                            <label className="form-label" htmlFor={`photo-${item.itemId}`} style={{ fontSize: '0.78rem' }}>
                                                Photo URL (optional)
                                            </label>
                                            <input
                                                id={`photo-${item.itemId}`}
                                                className="form-input"
                                                placeholder="https://…"
                                                value={pending[item.itemId]?.photoUrl ?? item.photoUrl ?? ''}
                                                onChange={(e) => setItemPhoto(item.itemId, e.target.value)}
                                                style={{ fontSize: '0.85rem' }}
                                            />
                                        </div>

                                        {(getItemResult(item) === 'fail' || getItemResult(item) === 'deficient') && (
                                            <div
                                                style={{
                                                    marginTop: '14px',
                                                    padding: '14px',
                                                    borderRadius: '10px',
                                                    background: 'rgba(192,57,43,0.06)',
                                                    border: '1px solid rgba(192,57,43,0.2)'
                                                }}
                                            >
                                                <div style={{ fontWeight: 800, fontSize: '0.85rem', marginBottom: '10px' }}>
                                                    Flag deficiency
                                                </div>
                                                <label className="form-label" htmlFor={`def-desc-${item.itemId}`}>
                                                    Description <span style={{ color: '#c0392b' }}>*</span>
                                                </label>
                                                <textarea
                                                    id={`def-desc-${item.itemId}`}
                                                    className="form-input"
                                                    rows={2}
                                                    placeholder="Describe the issue for quoting / remediation…"
                                                    value={deficiencyDraft[item.itemId]?.description ?? ''}
                                                    onChange={(e) =>
                                                        setDeficiencyDraft((prev) => ({
                                                            ...prev,
                                                            [item.itemId]: {
                                                                description: e.target.value,
                                                                severity: prev[item.itemId]?.severity ?? 'major'
                                                            }
                                                        }))
                                                    }
                                                    style={{ width: '100%', marginBottom: '10px' }}
                                                />
                                                <label className="form-label" htmlFor={`def-sev-${item.itemId}`}>Severity</label>
                                                <select
                                                    id={`def-sev-${item.itemId}`}
                                                    className="form-input"
                                                    style={{ maxWidth: '220px', marginBottom: '10px' }}
                                                    value={deficiencyDraft[item.itemId]?.severity ?? 'major'}
                                                    onChange={(e) =>
                                                        setDeficiencyDraft((prev) => ({
                                                            ...prev,
                                                            [item.itemId]: {
                                                                description: prev[item.itemId]?.description ?? '',
                                                                severity: e.target.value as 'critical' | 'major' | 'minor'
                                                            }
                                                        }))
                                                    }
                                                >
                                                    <option value="critical">Critical</option>
                                                    <option value="major">Major</option>
                                                    <option value="minor">Minor</option>
                                                </select>
                                                <label className="form-label" htmlFor={`def-file-${item.itemId}`}>
                                                    Photo upload (optional)
                                                </label>
                                                <input id={`def-file-${item.itemId}`} type="file" accept="image/*" />
                                                <div style={{ marginTop: '12px' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        disabled={!!deficiencyDraft[item.itemId]?.saving}
                                                        onClick={() => submitDeficiency(item.itemId)}
                                                        style={{ padding: '10px 18px', fontSize: '0.88rem' }}
                                                    >
                                                        {deficiencyDraft[item.itemId]?.saving
                                                            ? 'Saving…'
                                                            : deficiencyByItemId[item.itemId]
                                                              ? 'Update deficiency record'
                                                              : 'Save deficiency'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                            type="button"
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/technician/inspections')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={completeInspection}
                            disabled={!allAnswered || completing}
                            style={{
                                padding: '12px 28px',
                                opacity: allAnswered ? 1 : 0.45,
                                cursor: allAnswered ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {completing ? 'Completing…' : 'Complete Inspection'}
                        </button>
                    </div>

                    {!allAnswered && (
                        <p style={{ textAlign: 'right', color: 'var(--dash-muted)', fontSize: '0.85rem', marginTop: '8px' }}>
                            All items must have a result to complete the inspection.
                        </p>
                    )}
                    </>
                        )
                        : (
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                            type="button"
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/technician/inspections')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to inspections
                        </button>
                    </div>
                        )}
                </div>

                {devicePanelDevice ? (
                    <div
                        role="dialog"
                        aria-modal="true"
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            width: 'min(380px, 100vw)',
                            height: '100vh',
                            background: '#ffffff',
                            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
                            zIndex: 40,
                            padding: '72px 20px 24px',
                            overflowY: 'auto',
                            borderLeft: '1px solid rgba(0,0,0,0.08)'
                        }}
                    >
                        <button
                            type="button"
                            className="btn-secondary"
                            style={{ marginBottom: 16 }}
                            onClick={() => setDevicePanelDevice(null)}
                        >
                            Close panel
                        </button>
                        <h2 style={{ fontSize: '1.05rem', marginBottom: 8 }}>
                            {formatDeviceTypeLabel(devicePanelDevice.type)}
                        </h2>
                        <p style={{ margin: '0 0 12px', color: 'var(--dash-muted)', fontSize: '0.88rem' }}>
                            Registered asset for this building (read-only).
                        </p>
                        <dl style={{ display: 'grid', gridTemplateColumns: '118px 1fr', gap: '6px 12px', fontSize: '0.88rem' }}>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Model</dt>
                            <dd style={{ margin: 0 }}>{`${devicePanelDevice.manufacturer || ''} ${devicePanelDevice.modelName || ''}`.trim() || '—'}</dd>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}># / serial</dt>
                            <dd style={{ margin: 0 }}>{[devicePanelDevice.modelNumber, devicePanelDevice.serialNumber].filter(Boolean).join(' • ') || '—'}</dd>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Location</dt>
                            <dd style={{ margin: 0 }}>{devicePanelDevice.installationLocation || '—'}</dd>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Floor</dt>
                            <dd style={{ margin: 0 }}>{devicePanelDevice.floor || '—'}</dd>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Zone</dt>
                            <dd style={{ margin: 0 }}>{devicePanelDevice.zone || '—'}</dd>
                            <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Last result</dt>
                            <dd style={{ margin: 0, fontWeight: 800 }}>
                                {(devicePanelDevice.lastInspectionResult || 'not tested').replace(/_/g, ' ')}
                                {devicePanelDevice.lastInspectedAt
                                    ? ` · ${new Date(devicePanelDevice.lastInspectedAt).toLocaleDateString()}`
                                    : ''}
                            </dd>
                        </dl>
                        {devicePanelDevice.notes ? (
                            <>
                                <h3 style={{ fontSize: '0.85rem', marginTop: 16, marginBottom: 6 }}>Notes</h3>
                                <p style={{ fontSize: '0.88rem', lineHeight: 1.5, margin: 0 }}>{devicePanelDevice.notes}</p>
                            </>
                        ) : null}
                        {devicePanelDevice.photos && devicePanelDevice.photos.length > 0 ? (
                            <>
                                <h3 style={{ fontSize: '0.85rem', marginTop: 16, marginBottom: 8 }}>Photos</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    {devicePanelDevice.photos.map((url) => (
                                        <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={buildApiUrl(url)}
                                                alt=""
                                                style={{ width: 100, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                            />
                                        </a>
                                    ))}
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </AppShell>
    );
}
